// Shared helpers for the Landmark Website CMS dashboard

export const COLLECTIONS = [
  {
    key: "team",
    label: "Team",
    singular: "Team member",
    sitePath: (slug) => `/people/${slug}`,
    description: "People shown on the /people directory and profile pages.",
  },
  {
    key: "newsroom",
    label: "Newsroom",
    singular: "Article",
    sitePath: (slug) => `/newsroom/${slug}`,
    description: "Blog posts and news articles under /newsroom.",
  },
  {
    key: "portfolio",
    label: "Portfolio",
    singular: "Portfolio company",
    sitePath: () => "/portfolio",
    description: "Companies and deals shown on the /portfolio page.",
  },
  {
    key: "caseStudies",
    label: "Case studies",
    singular: "Case study",
    sitePath: (slug) => `/case-studies/${slug}`,
    description: "Full case study pages under /case-studies.",
  },
  {
    key: "caseCards",
    label: "Case study cards",
    singular: "Case study card",
    sitePath: () => "/case-studies",
    description: "The summary cards shown on the case studies grid.",
  },
  {
    key: "legal",
    label: "Legal",
    singular: "Legal page",
    sitePath: (slug) => `/${slug}`,
    description: "Privacy policy and terms of use.",
  },
];

export const getCollection = (key) => COLLECTIONS.find((c) => c.key === key);

// document key → path on the live site (for preview)
export const documentSitePath = (key) => {
  if (key === "home") return "/";
  if (key === "about") return "/about";
  if (key === "services-index") return "/services";
  if (key === "markets-index") return "/markets";
  if (key === "people-page") return "/people";
  if (key === "careers") return "/careers";
  if (key === "portfolio-page") return "/portfolio";
  if (key === "case-studies-page") return "/case-studies";
  if (key === "newsroom-page") return "/newsroom";
  if (key === "contact") return "/contact";
  if (key === "privacy-page") return "/privacy";
  if (key === "terms-page") return "/terms";
  if (key.startsWith("service:")) return `/services/${key.split(":")[1]}`;
  if (key.startsWith("market:")) return `/markets/${key.split(":")[1]}`;
  return "/";
};

export const slugify = (str) =>
  String(str || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// "heroImage" → "Hero image", "cta" → "CTA"
const ACRONYMS = { cta: "CTA", seo: "SEO", og: "OG", url: "URL", gtm: "GTM", faq: "FAQ", id: "ID" };
export const humanize = (key) => {
  const words = String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => ACRONYMS[w] || w);
  const label = words.join(" ");
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const formatTime = (unixSeconds) => {
  if (!unixSeconds) return "";
  return new Date(unixSeconds * 1000).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatDateOnly = (unixSeconds) => {
  if (!unixSeconds) return "";
  return new Date(unixSeconds * 1000).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

export const timeAgo = (unixSeconds) => {
  if (!unixSeconds) return "";
  const diff = Math.floor(Date.now() / 1000) - unixSeconds;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return formatDateOnly(unixSeconds);
};

export const deepClone = (obj) => JSON.parse(JSON.stringify(obj ?? null));

export const deepEqual = (a, b) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// path helpers for the structured editor ("hero.image.src", "values.2.title")
export const getAtPath = (obj, path) => {
  if (!path.length) return obj;
  return path.reduce((cur, key) => (cur == null ? undefined : cur[key]), obj);
};

export const setAtPath = (obj, path, value) => {
  const clone = deepClone(obj);
  let cur = clone;
  for (let i = 0; i < path.length - 1; i++) {
    cur = cur[path[i]];
  }
  cur[path[path.length - 1]] = value;
  return clone;
};

// does this key/value look like an image reference?
export const isImageKey = (key) =>
  /^(src|photo|logo|hero|image|heroimage|cardimage|acquirerlogo|defaultogimage|ogimage|icon)$/i.test(
    String(key).replace(/[-_]/g, "")
  );

export const isImagePathValue = (value) =>
  typeof value === "string" &&
  (/\.(png|jpe?g|webp|avif|gif|svg)(\?.*)?$/i.test(value) ||
    value.startsWith("/media/") ||
    value.includes("/uploads/landmark/website/"));

// is this a long-form text value (render textarea instead of input)?
export const isLongText = (key, value) =>
  /^(dek|text|bio|description|paragraph|excerpt|note|helper|blurb|oneliner|statement|caption|intro|message)s?$/i.test(
    String(key)
  ) ||
  (typeof value === "string" && value.length > 90);

export const isHtmlKey = (key) => /html$/i.test(String(key));

export const STATUS_LABELS = {
  open: "Open",
  "in-progress": "In progress",
  done: "Done",
  declined: "Declined",
};

export const PRIORITY_LABELS = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};
